/**
 * Retry policy for `POST /v1/api/tasks/schedule`.
 *
 * WHAT THIS IS
 *   A pure decision function: given the error a schedule attempt produced and
 *   which attempt it was, say whether to retry, how long to wait, or why to
 *   stop. It owns no timers, no HTTP and no UI — the caller schedules the wait.
 *
 * WHY IT EXISTS (evidence, 2026-09-05 Mongo write-stall incident)
 *   Today the schedule call uses `retry({count: 2, delay: 1000})` with an
 *   enforcement bail-out and nothing else (file-process.service.ts:145). That is:
 *     - a flat 1 s delay, no backoff and no jitter, so a batch that failed
 *       together retries together and hits the stalled primary as one wave;
 *     - ~92 s of wall clock (3 x the 30 s per-attempt timeout + 2 x 1 s), not
 *       the 30 s v5/v5.1 assumed (correction 17);
 *     - no `Retry-After` handling anywhere, so the server cannot ask the client
 *       to back off (correction 19);
 *     - a 409 is not an enforcement error, so a duplicate in-flight claim is
 *       retried blindly twice — 303 of 11,882 upload uuids ended up with
 *       duplicate workflows in 24 h;
 *     - and nothing ever concludes "give up": the card is simply removed.
 *
 *   This module is the replacement described in plan §4.5: jittered
 *   2 / 5 / 10 / 20 / 30 s, a 5-minute total budget, `Retry-After` honoured,
 *   the enforcement bail-out kept, 409-in-flight treated as retryable at the
 *   server's pace, and every other 4xx terminal.
 *
 * PLAN SECTION
 *   MONGO-STALL-PLAN-v5.2.md §4 (client blockers 5, 7) and §3 (item B, the
 *   409 + Retry-After contract); corrections 17, 19, 22.
 *   CORE-PIPELINE-REFERENCE.md §10 ("Schedule").
 *
 * WIRED 2026-09-05 behind `featureFlags.clientQueuedState`: the ladder in
 * `FileProcessService._scheduleLadderLoop` asks this for every verdict. With the
 * flag OFF `_scheduleTask` still uses the rxjs `retry(2, 1000)` config, byte for
 * byte.
 */

/** Base ladder, in ms. Plan §4.5: "jittered 2 / 5 / 10 / 20 / 30 s". */
export const SCHEDULE_RETRY_DELAYS_MS: readonly number[] = [2_000, 5_000, 10_000, 20_000, 30_000];

/** Plan §4.4: "give up at 5 min". Total wall clock, not per attempt. */
export const SCHEDULE_RETRY_BUDGET_MS = 5 * 60 * 1_000;

/**
 * Jitter is +/- 20% around the ladder value, so the ladder stays the median
 * (rng() === 0.5 yields the base delay exactly). Full jitter (0..base) was
 * rejected: it would let the first retry fire ~immediately, which is what the
 * flat 1 s policy already does wrong when a whole batch fails together.
 * Decision recorded 2026-09-05.
 */
export const SCHEDULE_RETRY_JITTER_RATIO = 0.2;

export type ScheduleRetryDecisionKind =
    /** Wait `delayMs`, then attempt again. */
    | 'retry'
    /** Enforcement / billing rejection: stop now, show the modal, keep the card. */
    | 'bail-enforcement'
    /** Deterministic client error: stop, no retry will ever help. */
    | 'terminal'
    /** The 5-minute budget is spent: stop retrying, offer "Retry all". */
    | 'budget-exhausted';

export interface ScheduleRetryDecision {
    kind: ScheduleRetryDecisionKind;
    /** Only set when `kind === 'retry'`. */
    delayMs?: number;
    /** True when `delayMs` came from the server's Retry-After, not the ladder. */
    fromRetryAfter: boolean;
    /** 1-based number of the attempt that just failed. */
    attempt: number;
    /** Machine-readable why, for logs and metrics. */
    reason: string;
}

/**
 * The parts of a failed schedule attempt this policy reads. Deliberately
 * structural: the module stays free of `ClientError` / `ServiceError` and of
 * anything that drags in rxjs, inversify or the DOM.
 */
export interface ScheduleFailure {
    status?: number;
    name?: string;
    /** Parsed response body, when there was one. */
    response?: unknown;
    /** Raw `Retry-After` header value: delta-seconds or an HTTP-date. */
    retryAfter?: string | number | null;
}

export interface ScheduleRetryPolicyOptions {
    /** Injectable clock; defaults to `Date.now`. */
    now?: () => number;
    /** Injectable RNG in [0, 1); defaults to `Math.random`. */
    random?: () => number;
    /** Override the ladder (tests, and a possible settings-sourced ladder later). */
    delaysMs?: readonly number[];
    /** Override the total budget. */
    budgetMs?: number;
    /** Override enforcement detection; defaults to the structural check below. */
    isEnforcementError?: (failure: ScheduleFailure) => boolean;
    /** When the batch started, for budget accounting; defaults to `now()`. */
    startedAtMs?: number;
}

/**
 * Structural mirror of `EnforcementErrorService.isEnforcementError`: HTTP 429
 * carrying `dataType: 'enforcement_error'`, or HTTP 402 carrying
 * `code: 'insufficient_credits'`.
 *
 * Duplicated rather than imported on purpose — importing the service would pull
 * inversify, rxjs and the `ClientError` module graph into what is meant to be a
 * pure function. The spec pins the two implementations against each other on
 * every canonical shape so the duplication cannot drift silently.
 */
export function isEnforcementShapedFailure(failure: ScheduleFailure): boolean {
    if (!failure || typeof failure !== 'object') return false;
    const { status, response } = failure;
    if (status !== 429 && status !== 402) return false;
    if (!response || typeof response !== 'object') return false;
    const body = response as { dataType?: unknown; code?: unknown };
    return body.dataType === 'enforcement_error' || body.code === 'insufficient_credits';
}

/**
 * Parse a `Retry-After` header value into a delay in ms, or null when it is
 * absent or unparseable. RFC 9110 allows either delta-seconds or an HTTP-date;
 * both are accepted. A date in the past clamps to 0, never to a negative wait.
 */
export function parseRetryAfterMs(
    value: string | number | null | undefined,
    nowMs: number,
): number | null {
    if (value === null || value === undefined || value === '') return null;

    if (typeof value === 'number') {
        return Number.isFinite(value) && value >= 0 ? Math.round(value * 1000) : null;
    }

    const trimmed = String(value).trim();
    if (trimmed === '') return null;

    // delta-seconds. Reject anything that is not a plain non-negative integer,
    // so "30s" or "-5" fall through rather than silently becoming 30 s / 0.
    if (/^\d+$/.test(trimmed)) {
        return Number(trimmed) * 1000;
    }

    // `Date.parse` is far too permissive to point at a raw header: V8's legacy
    // fallback parser turns "-5" into 2001-05-01, which would silently become a
    // 0 ms wait. Require something that actually looks like a date — a 4-digit
    // year and at least one letter (the day/month name of an IMF-fixdate, or
    // the T/Z of an ISO string) — before trusting it. Verified 2026-09-05.
    if (!/\d{4}/.test(trimmed) || !/[A-Za-z]/.test(trimmed)) return null;

    const parsed = Date.parse(trimmed);
    if (Number.isNaN(parsed)) return null;
    return Math.max(0, parsed - nowMs);
}

export type ScheduleFailureClass = 'enforcement' | 'in-flight' | 'terminal' | 'retryable';

export class ScheduleRetryPolicy {
    private readonly now: () => number;
    private readonly random: () => number;
    private readonly delaysMs: readonly number[];
    private readonly budgetMs: number;
    private readonly isEnforcement: (failure: ScheduleFailure) => boolean;
    private readonly startedAtMs: number;

    constructor(options: ScheduleRetryPolicyOptions = {}) {
        this.now = options.now ?? (() => Date.now());
        this.random = options.random ?? (() => Math.random());
        this.delaysMs = options.delaysMs ?? SCHEDULE_RETRY_DELAYS_MS;
        this.budgetMs = options.budgetMs ?? SCHEDULE_RETRY_BUDGET_MS;
        this.isEnforcement = options.isEnforcementError ?? isEnforcementShapedFailure;
        this.startedAtMs = options.startedAtMs ?? this.now();
    }

    /** Absolute instant at which retrying stops, whatever the ladder says. */
    get deadlineMs(): number {
        return this.startedAtMs + this.budgetMs;
    }

    /** How the failure is treated, independent of budget and attempt count. */
    classify(failure: ScheduleFailure): ScheduleFailureClass {
        if (this.isEnforcement(failure)) return 'enforcement';

        const status = failure?.status;

        // A duplicate in-flight claim (plan §3). The work may already be
        // happening server-side; retrying at the server's pace is what resolves
        // it, and item B guarantees the retry cannot create a second workflow.
        if (status === 409) return 'in-flight';

        // A rate limit that is NOT the enforcement envelope: standard backoff.
        if (status === 429) return 'retryable';

        // Every other deterministic client error. Retrying re-sends the same
        // body and gets the same answer.
        if (typeof status === 'number' && status >= 400 && status < 500) return 'terminal';

        // 5xx (including the planned 503 + Retry-After backpressure), and
        // transport failures with no status at all.
        return 'retryable';
    }

    /**
     * Decide what to do after `attempt` (1-based) failed with `failure`.
     */
    decide(failure: ScheduleFailure, attempt: number): ScheduleRetryDecision {
        const kindOf = this.classify(failure);
        const base = { attempt, fromRetryAfter: false };

        if (kindOf === 'enforcement') {
            return { ...base, kind: 'bail-enforcement', reason: 'enforcement-or-billing-rejection' };
        }
        if (kindOf === 'terminal') {
            return { ...base, kind: 'terminal', reason: `non-retryable-status-${failure?.status}` };
        }

        const nowMs = this.now();
        if (nowMs >= this.deadlineMs) {
            return { ...base, kind: 'budget-exhausted', reason: 'budget-spent-before-next-attempt' };
        }

        // The ladder runs out after its last rung; the budget, not the rung
        // count, is what ends retrying, so the last rung repeats.
        const ladderIndex = Math.min(Math.max(attempt, 1) - 1, this.delaysMs.length - 1);
        const retryAfterMs = parseRetryAfterMs(failure?.retryAfter, nowMs);

        // Retry-After overrides the ladder outright, jitter included: the server
        // has said when it wants to be called, and a 409 lease in particular is
        // only resolvable once it expires.
        const fromRetryAfter = retryAfterMs !== null;
        const delayMs = fromRetryAfter ? retryAfterMs! : this.jitter(this.delaysMs[ladderIndex]);

        if (nowMs + delayMs > this.deadlineMs) {
            return {
                ...base,
                kind: 'budget-exhausted',
                fromRetryAfter,
                reason: 'next-attempt-would-exceed-budget',
            };
        }

        return {
            ...base,
            kind: 'retry',
            delayMs,
            fromRetryAfter,
            reason: kindOf === 'in-flight' ? 'schedule-in-flight' : 'transient-failure',
        };
    }

    /** Base +/- SCHEDULE_RETRY_JITTER_RATIO, so the ladder value is the median. */
    private jitter(baseMs: number): number {
        const spread = SCHEDULE_RETRY_JITTER_RATIO;
        const factor = 1 - spread + 2 * spread * this.random();
        return Math.round(baseMs * factor);
    }
}

export function createScheduleRetryPolicy(
    options?: ScheduleRetryPolicyOptions,
): ScheduleRetryPolicy {
    return new ScheduleRetryPolicy(options);
}
